import { z } from 'zod'

/** The one shape a color is stored in: lowercase `#rrggbb`. */
export const HEX_COLOR_RE = /^#[0-9a-f]{6}$/

/**
 * A CSS color as `#rrggbb`, or null when there is nothing usable.
 *
 * Social Tables reports element colors as `rgb(r, g, b)`. Konva would render
 * that as-is, but a color input cannot show it, so a planner opening the picker
 * on an imported piece would see black and overwrite the color by touching it.
 *
 * It lives here rather than beside the importer because it is what *defines* the
 * stored form. The importer was the only caller for as long as it was the only
 * way a color could arrive; now the schemas below normalize through it, so a
 * color written by any future path is stored the same way as one imported.
 */
export function toHexColor(raw: string): string | null {
  const value = raw.trim().toLowerCase()
  if (HEX_COLOR_RE.test(value)) return value
  if (/^#[0-9a-f]{3}$/.test(value)) {
    const [, r, g, b] = value.split('')
    return `#${r}${r}${g}${g}${b}${b}`
  }
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+\s*)?\)$/.exec(value)
  if (!rgb) return null
  const channels = [rgb[1], rgb[2], rgb[3]].map((c) => Number(c))
  if (channels.some((c) => !Number.isFinite(c) || c < 0 || c > 255)) return null
  return `#${channels.map((c) => c.toString(16).padStart(2, '0')).join('')}`
}

/**
 * A color on a floor plan.
 *
 * **It normalizes before it validates**, which is the whole point: `#3C4D22`,
 * `#0af` and `rgb(0, 177, 248)` are all the same color written three ways, and
 * production holds all three — 1,136 zone strokes are uppercase alone. A bare
 * regex would have refused them. Anything it cannot read is left alone and
 * fails, which each field then decides what to do about.
 */
export const hexColorSchema = z.preprocess(
  (value) => (typeof value === 'string' ? (toHexColor(value) ?? value) : value),
  z.string().regex(HEX_COLOR_RE),
)

/**
 * A color a thing may simply not have.
 *
 * Tolerant on purpose: an unreadable color becomes *no* color — the thing
 * draws in the default ink — rather than failing its object. These schemas are
 * the read path as well as the write path, and an object that fails to parse is
 * dropped from the plan entirely. A plan is not allowed to lose a table because
 * somebody once stored `chartreuse`.
 */
export const optionalHexColorSchema = hexColorSchema.optional().catch(undefined)
